import { ROUTES } from './routes'

/**
 * VRG-661 — where `/` sends someone, decided in ONE pure function so it can be tested.
 *
 * `/` is the single post-auth decision point (VRG-339, see `HomePage.tsx`): every sign-in
 * path navigates there and this answers. It used to be two inline `<Navigate>`s in
 * HomePage, which is why the internal landing could not be covered by a guard spec.
 *
 * Internal staff land on Analytics "for now" (Spencer, 2026-10-05) — since VRG-684 its Website
 * page, `/internal/analytics/website`, addressed directly so it is one hop. Website is gated on
 * `internal_analytics_view`, which only platform_admin holds today (`seed.ts`), so a role
 * that cannot open it falls back to Companies, the page every internal role can open
 * (`InternalRoute.tsx` uses the same fallback for its gate 2). Without the fallback a
 * platform_manager would still end up on Companies, but via a second redirect inside
 * InternalRoute; asking here keeps it to one hop.
 */
export const INTERNAL_LANDING_ROUTE = ROUTES.INTERNAL_ANALYTICS_WEBSITE
export const INTERNAL_LANDING_FALLBACK_ROUTE = ROUTES.INTERNAL_COMPANIES

export type HomeLandingInput = {
  /**
   * Has an internal role (raw WorkOS role name). Passing the space-aware
   * `useIsInternalUser()` gives the same answer, because `inSpace` is checked here too.
   */
  isInternalRole: boolean
  /** A Vera staffer is currently operating inside a company's space. */
  inSpace: boolean
  /** `useInternalRouteAccess().canReachInternalPath`. Only consulted for internal staff. */
  canReachInternalPath: (pathname: string) => boolean
  /**
   * VRG-677: `useVeraSpaceStore().leaving`, the page "Return to Internal" is navigating
   * to. When set, a staffer who has just left a space is mid-navigation, and `/` must
   * agree with that navigation instead of racing it to Analytics.
   */
  leaving?: string | null
}

/**
 * Returns the path to redirect to, or `null` when the viewer should stay on Home (every
 * company user: Home is permanent, VRG-338).
 *
 * Order matters and mirrors the two redirects HomePage carried before this existed:
 * staff acting AS staff first, then staff-in-space (who operate as a brand user and land
 * on that brand's Settings, VERA-876).
 */
export const resolveHomeLanding = ({
  isInternalRole,
  inSpace,
  canReachInternalPath,
  leaving,
}: HomeLandingInput): string | null => {
  if (isInternalRole && !inSpace) {
    if (leaving) return leaving
    return canReachInternalPath(INTERNAL_LANDING_ROUTE)
      ? INTERNAL_LANDING_ROUTE
      : INTERNAL_LANDING_FALLBACK_ROUTE
  }
  if (inSpace) return ROUTES.SETTINGS_COMPANY_GENERAL
  return null
}

/**
 * VRG-677 — where a guard that bounces internal staff OFF a brand page sends them.
 *
 * Normally `fallback` (for `SettingsRoute`, the staffer's Account page). But in the render
 * right after "Return to Internal" the space is already gone while the router is still on
 * the brand page (the `leaving` doc in `useVeraSpaceStore` has the mechanics), and a
 * bounce to `fallback` there replaces the banner's own navigation. Bouncing to `leaving`
 * makes the two agree, so whichever lands last, the staffer ends up where they came from.
 */
export const resolveStaffBounce = (leaving: string | null | undefined, fallback: string): string =>
  leaving || fallback

/**
 * VRG-661 — should a page load end a persisted company space before the router runs?
 *
 * Yes when the URL being loaded is an internal one. Space mode is a localStorage session
 * (`useVeraSpaceStore`, persisted under `veraSpace`) that outlives the access token, which
 * is a browser-session cookie (`utils/storage.ts`). Loading `/internal/*` is an explicit
 * request to act as Vera staff; honouring a leftover space instead bounces the URL to the
 * brand's Settings (`InternalRoute` gate 1), which is the reported bug.
 *
 * In-app navigation never reaches this: entering a space from an internal page happens
 * after load, and `InternalRoute` / the VRG-369 `landing` handoff still own that.
 *
 * `isInternalPath` is injected rather than imported so this stays free of `utils` and can
 * be loaded directly by a guard spec; the real caller passes `utils/devSurface`'s.
 */
export const shouldEndSpaceOnLoad = (input: {
  pathname: string
  hasActiveSpace: boolean
  isInternalPath: (pathname: string) => boolean
}): boolean => input.hasActiveSpace && input.isInternalPath(input.pathname)
